import { cn } from '@web/lib/cn';

/**
 * The attention score and the flags behind it.
 *
 * The score is quantised into three bands for display AND for sorting: a
 * 0.3-point wobble must never reorder the board, and a number that is
 * meaningful to two decimal places is a number nobody can act on.
 */

export type AttentionBand = 'low' | 'raised' | 'urgent';

export function attentionBand(score: number): AttentionBand {
  if (score >= 70) return 'urgent';
  if (score >= 30) return 'raised';
  return 'low';
}

const BAND_LABEL: Record<AttentionBand, string> = {
  low: 'low attention',
  raised: 'raised attention',
  urgent: 'needs attention now',
};

export function AttentionScore({ score, className }: { score: number; className?: string }) {
  const band = attentionBand(score);
  return (
    <span
      className={cn('inline-flex items-center gap-1 font-mono text-xs tabular-nums', className)}
      aria-label={`attention ${String(Math.round(score))}, ${BAND_LABEL[band]}`}
    >
      {/* A caret, not just a colour: the one marker in the console that means
          "now", drawn in the one hue reserved for it. */}
      <span
        aria-hidden
        className={cn(
          'inline-block size-0 border-x-[4px] border-b-[6px] border-x-transparent',
          band === 'urgent' ? 'border-b-signal' : 'invisible border-b-transparent',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'w-6 text-right',
          band === 'urgent' && 'font-semibold text-signal',
          band === 'raised' && 'text-ink',
          band === 'low' && 'text-ink-faint',
        )}
      >
        {Math.round(score)}
      </span>
    </span>
  );
}

/**
 * The flags the scorer produced. Free-form strings on the wire, because the
 * server owns that vocabulary and is still growing it — known ones get a short
 * token, anything new is shown verbatim rather than swallowed.
 */
const FLAG_LABEL: Record<string, string> = {
  salary: 'SALARY',
  visa: 'VISA',
  angry: 'ANGRY',
  competitor: 'RIVAL',
  window_closing: 'WINDOW',
  'window-closing': 'WINDOW',
  long_wait: 'WAIT',
  unanswered: 'UNANS',
  document_rejected: 'DOCREJ',
  passport_expiring: 'PASSPT',
};

const FLAG_URGENT = new Set(['angry', 'window_closing', 'window-closing', 'document_rejected']);

export function AttentionFlags({
  flags,
  limit = 3,
  className,
}: {
  flags: string[];
  limit?: number;
  className?: string;
}) {
  if (flags.length === 0) return null;
  const shown = flags.slice(0, limit);
  const overflow = flags.length - shown.length;

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {shown.map((flag) => (
        <span
          key={flag}
          className={cn(
            'inline-flex h-[15px] items-center border px-1 font-mono text-[9px] tracking-[0.06em] whitespace-nowrap',
            FLAG_URGENT.has(flag)
              ? 'border-signal text-signal'
              : 'border-rule-strong text-ink-faint',
          )}
        >
          {FLAG_LABEL[flag] ?? flag.slice(0, 6).toUpperCase()}
        </span>
      ))}
      {overflow > 0 && (
        <span className="font-mono text-[9px] text-ink-faint tabular-nums">+{overflow}</span>
      )}
    </span>
  );
}
